'use client'
import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { LinePlus } from '@qr-front/icons/line-plus'
import { Button } from '@qr-front/ui/components/Button'
import { useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { transformBackendResponseKey } from '@/config'
import { URL_MAX_LENGTH } from '@/constants'
import { useSyncFieldWithAtom } from '@/hooks'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { maxLength, validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import { RhcField } from '../../components/RhcField'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import { ButtonsSection, facebookButtonListAtom } from './ButtonsSection'
import {
	getFacebookInfoFormSchema,
	TFacebookInfoFields,
} from './FacebookInfoFormModel'

export const FACEBOOK_FORM_ID = 'facebook-form'

export const facebookUrlAtom = atomWithReset<string | undefined>('')
export const facebookNameAtom = atomWithReset<string | undefined>(undefined)
export const facebookTitleAtom = atomWithReset('')
export const facebookWebsiteAtom = atomWithReset<string | undefined>('')

export const resetFacebookForm = () => {
	getStore().set(facebookUrlAtom, RESET)
	getStore().set(facebookNameAtom, RESET)
	getStore().set(facebookTitleAtom, RESET)
	getStore().set(facebookWebsiteAtom, RESET)
	getStore().set(facebookButtonListAtom, RESET)
}

export const FacebookForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	const form = useForm<TFacebookInfoFields>({
		defaultValues: {
			url: getStore().get(facebookUrlAtom),
			name: getStore().get(facebookNameAtom),
			title: getStore().get(facebookTitleAtom),
			website: getStore().get(facebookWebsiteAtom),
			buttons: getStore().get(facebookButtonListAtom),
		},
		mode: 'onChange',
		resolver: yupResolver(getFacebookInfoFormSchema(t)),
		reValidateMode: 'onChange',
		shouldUnregister: false,
	})

	const {
		formState: { isValid },
		handleSubmit,
	} = form

	useSyncFormSectionValidity({ formId: FACEBOOK_FORM_ID, isValid })

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				property = property.replace('info.', '')
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	const { getValues, setFocus, setValue } = form

	useSyncFieldWithAtom({
		atom: facebookButtonListAtom,
		setFieldValue: (value) => setValue('buttons', value),
	})

	const facebookUrlField = (
		<RhcField
			type="url"
			required
			form={form}
			path="url"
			fieldName="url"
			atom={facebookUrlAtom}
			label={t('generator__content_form_section__facebook__url__label')}
			placeholder={t(
				'generator__content_form_section__facebook__url__placeholder',
			)}
			onChangeTransform={(event) => maxLength(URL_MAX_LENGTH + 1)(event)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const nameField = (
		<RhcField
			type="text"
			form={form}
			path="name"
			fieldName="name"
			atom={facebookNameAtom}
			label={t('generator__content_form_section__facebook__name__label')}
			placeholder={t(
				'generator__content_form_section__facebook__name__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const titleField = (
		<RhcField
			type="text"
			form={form}
			path="title"
			required
			fieldName="title"
			atom={facebookTitleAtom}
			label={t(
				'generator__content_form_section__videos_information__headline__label',
			)}
			placeholder={t(
				'generator__content_form_section__facebook__title__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const websiteField = (
		<RhcField
			type="url"
			form={form}
			path="website"
			fieldName="website"
			atom={facebookWebsiteAtom}
			label={t(
				'generator__content_form_section__images_information__website__label',
			)}
			placeholder={t(
				'generator__content_form_section__facebook__website__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const setButtonList = useSetAtom(facebookButtonListAtom)

	const handleClickAddButton = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}
		setButtonList((prev) => [...prev, { title: '', url: '' }])
		setTimeout(
			() => setFocus(`buttons.${getValues('buttons')!.length - 1}.title`),
			0,
		)
	}

	return (
		<fieldset>
			<div className="grid grid-cols-2 gap-x-8 gap-y-4">
				{facebookUrlField}
				{nameField}
				{titleField}
				{websiteField}
			</div>

			<FormProvider {...form}>
				<ButtonsSection />
			</FormProvider>

			<Button
				visual="soft"
				onPointerDown={handleClickAddButton}
				className="mt-6 tablet:mt-8 [&>svg]:size-4"
			>
				<LinePlus />
				{t(
					'generator__content_form_section__images_information__add_button',
				)}
			</Button>
		</fieldset>
	)
})

FacebookForm.displayName = 'FacebookForm'

export const facebookFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: FACEBOOK_FORM_ID,
	title: t('generator__content_form_section__facebook__title'),
	description: t('generator__content_form_section__facebook__description'),
	icon: <LineInfoCircle />,
	Content: FacebookForm,
})
